import React, { useState, useRef } from 'react';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import { companyInfo } from '../styles/variables';

const EMAILJS_SERVICE_ID  = 'service_f5bbpdp';
const EMAILJS_TEMPLATE_ID = 'template_kwvqiq6';
const EMAILJS_PUBLIC_KEY  = 'VgaBcXpb6Q_789jXT';
const RECAPTCHA_SITE_KEY  = '6LdRQeAsAAAAAMZrasLh0BJlNMYpmfZxMTMwxWQk'; // ← replace with your reCAPTCHA site key

const Contact = () => {
  const { contact } = companyInfo;

  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', product: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState('');
  const [captchaDone, setCaptchaDone] = useState(false);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaDone) {
      setError('Please complete the CAPTCHA before submitting.');
      return;
    }
    setError('');
    setSending(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name:    form.name,
          company: form.company,
          email:   form.email,
          phone:   form.phone,
          product: form.product,
          message: form.message,
          time:    new Date().toLocaleString(),
        },
        EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      recaptchaRef.current?.reset();
      setCaptchaDone(false);
    } finally {
      setSending(false);
    }
  };

  const inputStyle = (name) => ({
    width: '100%',
    background: focused === name ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.05)',
    border: focused === name ? '1px solid rgba(184,150,90,0.6)' : '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px',
    padding: '0.85rem 1.1rem',
    color: 'rgba(255,255,255,0.9)',
    fontFamily: "'Jost', sans-serif",
    fontSize: '0.9rem',
    fontWeight: 300,
    outline: 'none',
    transition: 'border 0.2s, background 0.2s',
    boxSizing: 'border-box',
  });

  const labelStyle = {
    display: 'block',
    fontFamily: "'Jost', sans-serif",
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
    marginBottom: '0.45rem',
  };

  return (
    <section className="contact-section" id="contact" style={{ padding: '2.5rem 0.5rem' }}>
      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div className="section-header">
          <div className="section-eyebrow">Get In Touch</div>
          <h2 className="section-title">Contact Us</h2>
          <div className="gold-divider">
            <div className="gold-divider-diamond" />
          </div>
          <p className="section-sub">
            Ready to discuss your requirements? Reach out to us for samples, pricing, and custom orders.
          </p>
        </div>

        {/* 3-column grid on desktop: info | form | locations */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr 1fr',
          gap: '2rem',
          position: 'relative',
          zIndex: 1,
        }} className="contact-3col">

          {/* ── Column 1: Contact Details ── */}
          <div className="contact-card">
            <div className="contact-ceo-box">
              <div className="contact-ceo-label">Chief Executive Officer</div>
              <div className="contact-ceo-name">{contact.ceo}</div>
            </div>

            <div className="contact-card-title">Contact Details</div>

            <div className="contact-item">
              <div className="contact-item-label">Phone</div>
              <div className="contact-item-value">
                <a href={`tel:${contact.phone}`}>{contact.phone}</a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-label">Mobile / WhatsApp</div>
              <div className="contact-item-value">
                <a href={`https://maps.google.com/maps?q=30.1809711,71.504837&z=15&output=embed, '')}`} target="_blank" rel="noreferrer">
                  {contact.mobile}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-label">Website</div>
              <div className="contact-item-value">
                <a href={`https://${contact.website}`} target="_blank" rel="noreferrer">
                  {contact.website}
                </a>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-item-label">Email Addresses</div>
              {contact.emails.map((email) => (
                <div key={email} className="contact-item-value">
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              ))}
            </div>
          </div>

          {/* ── Column 2: Query Form ── */}
          <div className="contact-card" style={{ borderColor: 'rgba(184,150,90,0.25)' }}>
            <div className="contact-card-title">Send a Query</div>

            {submitted ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: '1rem', padding: '3rem 0', textAlign: 'center',
              }}>
                <div style={{ fontSize: '2.5rem' }}>✓</div>
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: '1.5rem', fontWeight: 400, color: '#D4AF7A',
                }}>Thank You!</div>
                <p style={{
                  fontFamily: "'Jost', sans-serif", fontSize: '0.9rem', fontWeight: 300,
                  color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, maxWidth: '280px',
                }}>
                  Your query has been received. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: '', company: '', email: '', phone: '', product: '', message: '' });
                    setCaptchaDone(false);
                    recaptchaRef.current?.reset();
                  }}
                  style={{
                    marginTop: '0.5rem',
                    fontFamily: "'Jost', sans-serif", fontSize: '0.75rem', fontWeight: 600,
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: '#D4AF7A', background: 'transparent',
                    border: '1px solid rgba(212,175,122,0.4)',
                    borderRadius: '999px', padding: '0.6rem 1.4rem', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,122,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Full Name *</label>
                    <input
                      name="name" value={form.name} onChange={handleChange} required
                      placeholder="Your name"
                      style={inputStyle('name')}
                      onFocus={() => setFocused('name')} onBlur={() => setFocused('')}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Company</label>
                    <input
                      name="company" value={form.company} onChange={handleChange}
                      placeholder="Company name"
                      style={inputStyle('company')}
                      onFocus={() => setFocused('company')} onBlur={() => setFocused('')}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input
                      name="email" type="email" value={form.email} onChange={handleChange} required
                      placeholder="you@company.com"
                      style={inputStyle('email')}
                      onFocus={() => setFocused('email')} onBlur={() => setFocused('')}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone / WhatsApp</label>
                    <input
                      name="phone" value={form.phone} onChange={handleChange}
                      placeholder="+1 234 567 890"
                      style={inputStyle('phone')}
                      onFocus={() => setFocused('phone')} onBlur={() => setFocused('')}
                    />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Product Interest</label>
                  <select
                    name="product" value={form.product} onChange={handleChange}
                    style={{ ...inputStyle('product'), appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}
                    onFocus={() => setFocused('product')} onBlur={() => setFocused('')}
                  >
                    <option value="" style={{ background: '#2C2C2C' }}>Select a product category</option>
                    <option value="kitchen-towels" style={{ background: '#2C2C2C' }}>Kitchen Sets</option>
                    <option value="dobby-weave" style={{ background: '#2C2C2C' }}>Dobby Weave Kitchen Towels</option>
                    <option value="jacquard" style={{ background: '#2C2C2C' }}>Jacquard Weave Kitchen Towels</option>
                    <option value="terry" style={{ background: '#2C2C2C' }}>Terry Kitchen Towels</option>
                    <option value="flat-weave" style={{ background: '#2C2C2C' }}>Flat Weave Kitchen Towels</option>
                    <option value="printed" style={{ background: '#2C2C2C' }}>Printed Kitchen Towels</option>
                    <option value="dishcloth" style={{ background: '#2C2C2C' }}>Dish Cloth</option>
                    <option value="other" style={{ background: '#2C2C2C' }}>Other / Custom</option>
                  </select>
                </div>

                <div>
                  <label style={labelStyle}>Message *</label>
                  <textarea
                    name="message" value={form.message} onChange={handleChange} required
                    placeholder="Tell us about your requirements — quantities, specifications, delivery timeline..."
                    rows={5}
                    style={{ ...inputStyle('message'), resize: 'vertical', minHeight: '110px', lineHeight: 1.7 }}
                    onFocus={() => setFocused('message')} onBlur={() => setFocused('')}
                  />
                </div>

                {/* reCAPTCHA */}
                <div style={{ marginTop: '0.25rem' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(val) => setCaptchaDone(!!val)}
                    onExpired={() => setCaptchaDone(false)}
                    theme="dark"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <div style={{
                    fontFamily: "'Jost', sans-serif", fontSize: '0.82rem',
                    color: '#ff6b6b', background: 'rgba(255,107,107,0.1)',
                    border: '1px solid rgba(255,107,107,0.3)',
                    borderRadius: '8px', padding: '0.75rem 1rem',
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  style={{
                    fontFamily: "'Jost', sans-serif",
                    fontSize: '0.82rem', fontWeight: 600,
                    letterSpacing: '0.13em', textTransform: 'uppercase',
                    color: '#fff',
                    background: sending
                      ? 'rgba(184,150,90,0.5)'
                      : 'linear-gradient(135deg, #B8965A, #8B6E3A)',
                    border: 'none', borderRadius: '999px',
                    padding: '1rem 2rem',
                    cursor: sending ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.2s, transform 0.2s',
                    marginTop: '0.25rem',
                  }}
                  onMouseEnter={e => { if (!sending) { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-1px)'; }}}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {sending ? 'Sending...' : 'Submit Query →'}
                </button>
              </form>
            )}
          </div>

          {/* ── Column 3: Office Locations ── */}
          <div className="contact-card">
            <div className="contact-card-title">Office Locations</div>

            <div className="contact-item">
              <div className="contact-item-label">Head Office</div>
              <div className="contact-item-value">{contact.headOffice}</div>
            </div>

            <div className="contact-item" style={{ marginTop: '1.5rem' }}>
              <div className="contact-item-label">Production Factory</div>
              <div className="contact-item-value">{contact.factory}</div>
            </div>

            {/* Map placeholder */}
            <div style={{
              marginTop: '2rem', borderRadius: '12px', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.1)', height: '160px',
              background: 'rgba(255,255,255,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: '0.5rem', cursor: 'pointer',
              transition: 'background 0.2s',
            }}
              onClick={() => window.open('https://maps.google.com/?q=Multan+Pakistan', '_blank')}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            >
              <span style={{ fontSize: '2rem' }}>📍</span>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.06em' }}>
                Multan, Punjab, Pakistan
              </span>
              <span style={{ fontFamily: 'var(--font-accent)', fontSize: '0.6rem', color: 'var(--color-gold-light)', letterSpacing: '0.14em' }}>
                VIEW ON MAP →
              </span>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={`https://wa.me/${contact.mobile.replace(/\s+/g, '')}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                marginTop: '1.25rem',
                background: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.25)',
                borderRadius: '10px', padding: '0.85rem',
                fontFamily: "'Jost', sans-serif", fontSize: '0.8rem', fontWeight: 600,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: '#25D366', textDecoration: 'none',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(37,211,102,0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(37,211,102,0.12)'}
            >
              <span style={{ fontSize: '1.1rem' }}>💬</span> Chat on WhatsApp
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .contact-3col {
            grid-template-columns: 1fr 1fr !important;
          }
          .contact-3col > div:nth-child(2) {
            grid-column: 1 / -1;
          }
        }
        @media (max-width: 640px) {
          .contact-3col {
            grid-template-columns: 1fr !important;
          }
          .contact-3col > div:nth-child(2) {
            grid-column: auto;
          }
        }
      `}</style>
    </section>
  );
};

export default Contact;